import React from 'react';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid';
import { RoleFolderPermissionDto } from '../../types/Role';
import { Box, Checkbox } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useUser } from '../../context/UserContext';
import UserMultiSelectCell from '../../components/ui/UserMultiSelectCell';
import { dataGridHeaderHeight, dataGridRowHeight } from '../../data/constants';
import { GLOBALMESSAGES } from '../../constants/messages';


interface PermissionGridProps {
  permissions: RoleFolderPermissionDto[];
  onPermissionChange: (roleId: number, field: keyof RoleFolderPermissionDto, checked: boolean) => void;
  onUserChange: (roleId: number, selectedUserIds: number[]) => void;
  columnVisibilityModel?: Record<string, boolean>;
  disableAll: boolean;
}

const CheckboxCell: React.FC<{
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  return <Checkbox size="small" checked={value}
    onChange={(e) => onChange(e.target.checked)}
    disabled={disabled} />;
};

const renderCheckboxCell = (
  field: keyof RoleFolderPermissionDto,
  onPermissionChange: (roleId: number, field: keyof RoleFolderPermissionDto, checked: boolean
  ) => void,
  disabledAll: boolean
) => {
  const Component = (params: { row: RoleFolderPermissionDto }) => {
    const checked = !!params.row[field];
    const users = params.row.users || [];
    // Check if any user is checked
    const anyUserChecked = users.some(user => user.isChecked);
    return (
      <CheckboxCell
        value={anyUserChecked ? checked : false}
        onChange={(checked: boolean) => onPermissionChange(params.row.roleId, field, checked)}
        disabled={disabledAll || !anyUserChecked}
      />
    );
  };
  Component.displayName = 'RenderCheckboxCell';
  return Component;
};

const PermissionGrid: React.FC<PermissionGridProps> = ({ permissions, onPermissionChange,
  onUserChange,
  columnVisibilityModel, disableAll }) => {

  const { userId } = useUser();

  const hiddenFields = ['roleId'];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };


  const columns: GridColDef[] = [
    { field: 'roleId', headerName: 'Role ID', width: 100, filterable: false },
    { field: 'roleName', headerName: 'Role Name', width: 150 },
    {
      field: "users",
      headerName: "Users",
      minWidth: 250,
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{
          height: '100%', display: 'flex', alignItems: 'center', overflow: "visible", position: "relative"
        }}>
          <UserMultiSelectCell
            value={params.value}
            row={params.row}
            userId={userId}
            onUserChange={onUserChange}
            disabled={disableAll}
          />
        </Box>
      ),
    },
    {
      field: 'canView',
      headerName: 'Can View',
      width: 120,
      renderCell: renderCheckboxCell('canView', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canDownload',
      headerName: 'Can Download',
      width: 120,
      renderCell: renderCheckboxCell('canDownload', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canEdit',
      headerName: 'Can Edit',
      width: 120,
      renderCell: renderCheckboxCell('canEdit', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canDelete',
      headerName: 'Can Delete',
      width: 120,
      renderCell: renderCheckboxCell('canDelete', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canShareToUser',
      headerName: 'Can Share to User',
      width: 150,
      renderCell: renderCheckboxCell('canShareToUser', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canShareToRole',
      headerName: 'Can Share to Role',
      width: 150,
      renderCell: renderCheckboxCell('canShareToRole', onPermissionChange, disableAll),
      filterable: false,
    },
    {
      field: 'canManagePermissions',
      headerName: 'Can Manage Permissions',
      width: 180,
      renderCell: renderCheckboxCell('canManagePermissions', onPermissionChange, disableAll),
      filterable: false,
    },
  ];
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumnFields>({
    left: ['roleName'],
  });

  return (
    <div style={{ height: 300, width: '100%' }}>
      <DataGridPro
        rows={permissions || []}
        rowHeight={dataGridRowHeight}
        columns={columns}
        hideFooterPagination
        pageSizeOptions={[]}
        getRowId={(row) => row.roleId || 0}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
        disableColumnSelector={true}
        columnHeaderHeight={dataGridHeaderHeight}
        className="default-data-grid"
        localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
        hideFooterSelectedRowCount={true}
        slotProps={{
          columnsManagement: {
            getTogglableColumns
          },
        }}
        initialState={{
        sorting: {
            sortModel: [{ field: 'roleId', sort: 'asc' }],
          },
        }}
      />
    </div>
  );
};

export default React.memo(PermissionGrid);
